/**
 * AiRefactorComparisonDialog - Side-by-side comparison of original vs AI suggestion
 * 
 * Features:
 * - Two-column layout showing original and AI-refactored content
 * - Accept/Reject buttons
 * - Loading state during AI processing
 * - History of previous suggestions
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  SmartToy as AiIcon,
  Check as AcceptIcon,
  Close as RejectIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as AcceptedIcon,
  Cancel as RejectedIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import apiClient from '../../services/api';
import toast from 'react-hot-toast';

interface AiSuggestion {
  suggestion_id: string;
  original_html: string;
  ai_html: string;
  status: string;
  created_at: string;
  request_number: number;
  remaining_requests: number;
  ai_metadata?: {
    examples_used?: number;
    processing_time_ms?: number;
  };
}

interface HistoryItem {
  id: string;
  status: string;
  created_at: string;
  reviewed_at?: string;
  request_number: number;
  original_preview?: string;
  ai_preview?: string;
}

interface AiRefactorComparisonDialogProps {
  open: boolean;
  onClose: () => void;
  reportId: string;
  currentContent: string; // eslint-disable-line @typescript-eslint/no-unused-vars
  onAccept: (newContent: string) => void;
}

const AiRefactorComparisonDialog: React.FC<AiRefactorComparisonDialogProps> = ({
  open,
  onClose,
  reportId,
  currentContent: _currentContent,
  onAccept,
}) => {
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState<AiSuggestion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [remainingRequests, setRemainingRequests] = useState<number | null>(null);

  // Request AI refactoring when dialog opens
  useEffect(() => {
    if (open && reportId && !suggestion) {
      requestAiRefactor();
    }
  }, [open, reportId]);

  // Load history when expanded
  useEffect(() => {
    if (showHistory && history.length === 0) {
      loadHistory();
    }
  }, [showHistory]);

  const requestAiRefactor = async () => {
    setLoading(true);
    setError(null);
    setSuggestion(null);

    try {
      const response = await apiClient.post(`/reports/${reportId}/ai-refactor`, {});
      
      if (response.status === 200 || response.status === 201) {
        setSuggestion(response.data);
        setRemainingRequests(response.data.remaining_requests);
      }
    } catch (err: any) {
      console.error('AI refactor error:', err);
      
      if (err.response?.status === 429) {
        setError('Ai atins limita maximă de 5 refactorizări pentru acest raport.');
      } else if (err.response?.status === 504) {
        setError('Serviciul AI a expirat. Te rog încearcă din nou.');
      } else if (err.response?.status === 503) {
        setError('Serviciul de refactorizare AI nu este disponibil momentan.');
      } else {
        setError(err.response?.data?.detail || 'Eroare la procesarea cererii AI.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await apiClient.get(`/reports/${reportId}/ai-refactor/history`);
      if (response.status === 200) {
        setHistory(response.data.suggestions || []);
        setRemainingRequests(response.data.remaining_suggestions);
      }
    } catch (err) {
      console.error('Failed to load AI history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!suggestion) return;
    
    setAccepting(true);
    try {
      const response = await apiClient.post(`/reports/${reportId}/ai-refactor/accept`, {
        suggestion_id: suggestion.suggestion_id,
      });
      
      if (response.status === 200) {
        toast.success('Sugestia AI a fost aplicată!');
        onAccept(suggestion.ai_html);
        onClose();
      }
    } catch (err: any) {
      console.error('Accept error:', err);
      toast.error(err.response?.data?.detail || 'Eroare la aplicarea sugestiei.');
    } finally {
      setAccepting(false);
    }
  };

  const handleReject = async () => {
    if (!suggestion) return;
    
    setRejecting(true);
    try {
      const response = await apiClient.post(`/reports/${reportId}/ai-refactor/reject`, {
        suggestion_id: suggestion.suggestion_id,
      });
      
      if (response.status === 200) {
        toast.success('Sugestia AI a fost respinsă.');
        onClose();
      }
    } catch (err: any) {
      console.error('Reject error:', err);
      toast.error(err.response?.data?.detail || 'Eroare la respingerea sugestiei.');
    } finally {
      setRejecting(false);
    }
  };

  const handleClose = () => {
    // Reset state on close
    setSuggestion(null);
    setError(null);
    setShowHistory(false);
    onClose();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted':
        return <AcceptedIcon color="success" fontSize="small" />;
      case 'rejected':
        return <RejectedIcon color="error" fontSize="small" />;
      default:
        return <PendingIcon color="warning" fontSize="small" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'Acceptat';
      case 'rejected':
        return 'Respins';
      default:
        return 'În așteptare';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '70vh',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <AiIcon color="warning" />
        <Typography variant="h6" component="span">
          Refactorizare AI
        </Typography>
        <Chip
          label="EXPERIMENTAL"
          size="small"
          color="warning"
          sx={{ fontWeight: 'bold' }}
        />
        {remainingRequests !== null && (
          <Chip
            label={`${remainingRequests} încercări rămase`}
            size="small"
            color={remainingRequests > 2 ? 'success' : remainingRequests > 0 ? 'warning' : 'error'}
            sx={{ ml: 'auto' }}
          />
        )}
      </DialogTitle>

      <DialogContent dividers>
        {/* Experimental Warning */}
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Funcție experimentală!</strong> AI-ul poate face greșeli. 
          Verificați cu atenție textul înainte de a accepta. 
          Termenii medicali pot fi alterați incorect.
        </Alert>
        
        {/* Loading State */}
        {loading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 300,
              gap: 2,
            }}
          >
            <CircularProgress size={48} />
            <Typography variant="body1" color="text.secondary">
              Se procesează raportul cu AI...
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Aceasta poate dura până la 60 de secunde
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Comparison View */}
        {suggestion && !loading && (
          <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
            {/* Original Column */}
            <Paper
              elevation={2}
              sx={{
                flex: 1,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '60vh',
                overflow: 'hidden',
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                color="text.secondary"
                gutterBottom
              >
                📄 Text Original
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  fontSize: '0.95rem',
                  lineHeight: 1.6,
                  '& p': { mb: 1 },
                }}
                dangerouslySetInnerHTML={{ __html: suggestion.original_html }}
              />
            </Paper>

            {/* AI Suggestion Column */}
            <Paper
              elevation={2}
              sx={{
                flex: 1,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '60vh',
                overflow: 'hidden',
                border: '2px solid',
                borderColor: 'primary.main',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AiIcon color="primary" fontSize="small" />
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color="primary"
                >
                  Sugestie AI
                </Typography>
                {suggestion.ai_metadata?.processing_time_ms && (
                  <Chip
                    label={`${(suggestion.ai_metadata.processing_time_ms / 1000).toFixed(1)}s`}
                    size="small"
                    variant="outlined"
                    sx={{ ml: 'auto' }}
                  />
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  fontSize: '0.95rem',
                  lineHeight: 1.6,
                  '& p': { mb: 1 },
                }}
                dangerouslySetInnerHTML={{ __html: suggestion.ai_html }}
              />
            </Paper>
          </Box>
        )}

        {/* History Section */}
        {!loading && (
          <Box sx={{ mt: 2 }}>
            <Button
              startIcon={showHistory ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              endIcon={<HistoryIcon />}
              onClick={() => setShowHistory(!showHistory)}
              size="small"
              color="inherit"
            >
              Istoric sugestii ({history.length})
            </Button>
            
            <Collapse in={showHistory}>
              {historyLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : history.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  Nu există sugestii anterioare.
                </Typography>
              ) : (
                <List dense>
                  {history.map((item) => (
                    <ListItem key={item.id} divider>
                      <ListItemIcon>
                        {getStatusIcon(item.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">
                              Cerere #{item.request_number}
                            </Typography>
                            <Chip
                              label={getStatusLabel(item.status)}
                              size="small"
                              color={
                                item.status === 'accepted'
                                  ? 'success'
                                  : item.status === 'rejected'
                                  ? 'error'
                                  : 'warning'
                              }
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {new Date(item.created_at).toLocaleString('ro-RO')}
                            {item.reviewed_at && ` • Revizuit: ${new Date(item.reviewed_at).toLocaleString('ro-RO')}`}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Collapse>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        {/* Retry button if error */}
        {error && !loading && remainingRequests !== 0 && (
          <Button
            onClick={requestAiRefactor}
            variant="outlined"
            color="primary"
          >
            Încearcă din nou
          </Button>
        )}

        <Box sx={{ flex: 1 }} />

        <Button
          onClick={handleClose}
          disabled={accepting || rejecting}
          color="inherit"
        >
          Anulează
        </Button>

        {suggestion && (
          <>
            <Button
              onClick={handleReject}
              disabled={accepting || rejecting}
              color="error"
              variant="outlined"
              startIcon={rejecting ? <CircularProgress size={16} /> : <RejectIcon />}
            >
              Respinge
            </Button>

            <Button
              onClick={handleAccept}
              disabled={accepting || rejecting}
              color="success"
              variant="contained"
              startIcon={accepting ? <CircularProgress size={16} color="inherit" /> : <AcceptIcon />}
            >
              Acceptă și Aplică
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AiRefactorComparisonDialog;

